/** One image field: preview + Upload + Brand library + Remove.
 *  Uploading hits the EXISTING Gift Generator upload routes, so the file lands
 *  on the same storage path Voucher Settings uses and keeps serving from the
 *  same URL it does today.
 *
 *  `libraryKind` turns on the one shared prompt: the image is already in use,
 *  and the owner is asked once whether to keep a copy in the Brand library.
 *  "Not now" does nothing at all and never asks again for that upload.
 *  `libraryNote` is for the fields whose upload already lands in the library —
 *  those are never asked, they just say so. */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../../../lib/session';
import { btn } from './buttons';

const API_BASE = (typeof window !== 'undefined' ? window.location.origin : '');
export const abs = (u) => (!u ? null : (String(u).startsWith('http') ? u : `${API_BASE}${u}`));

export const ImageField = ({ label, hint, value, endpoint, fileField = 'image',
                            responseKey, orgId, setupId, onChange, onLibrary,
                            libraryKind, libraryNote, libraryName, testid,
                            apiPrefix = 'voucher-settings', extraFields = null }) => {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const [offer, setOffer] = useState(null);     // the file just uploaded
    const [saving, setSaving] = useState(false);
    const input = useRef(null);

    /** What actually went wrong, in the venue's own language. The server knows
     *  (an expired session, an unreadable photo, a busy object store); this
     *  used to throw that away and blame the picture every time. */
    const uploadReason = async (res) => {
        let detail = '';
        try {
            detail = String(((await res.json()) || {}).detail || '');
        } catch (_e) {
            detail = '';
        }
        const says = (key) => ({ said: t(`imageUpload.${key}`) });
        if (res.status === 401 || res.status === 403) return says('sessionExpired');
        if (res.status === 413) return says('tooBig');
        if (res.status >= 500) return says('storageBusy');
        if (/could not be read/i.test(detail)) return says('unreadable');
        if (/not an image/i.test(detail)) return says('notImage');
        if (/came through empty/i.test(detail)) return says('empty');
        if (/under \d+\s*MB/i.test(detail)) return says('tooBig');
        return { said: t('imageUpload.failed', { field: label.toLowerCase() }) };
    };

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        setOffer(null);
        try {
            const form = new FormData();
            form.append('organization_id', orgId);
            if (setupId) form.append('setup_id', setupId);
            Object.entries(extraFields || {}).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') form.append(k, v);
            });
            form.append(fileField, file);
            const res = await fetch(`${API_BASE}/api/${apiPrefix}/${endpoint}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${getToken()}` },
                body: form,
            });
            if (!res.ok) throw await uploadReason(res);
            const data = await res.json();
            onChange(data[responseKey]);
            toast.success(t('imageUpload.ok', { field: label }));
            if (libraryKind) setOffer(file);
        } catch (err) {
            toast.error(err?.said || t('imageUpload.failed', { field: label.toLowerCase() }));
        } finally {
            setBusy(false);
            if (input.current) input.current.value = '';
        }
    };

    /** A second, immutable copy in the library. The image in use is NOT moved
     *  and its URL does not change. */
    const addToLibrary = async () => {
        if (!offer) return;
        setSaving(true);
        try {
            const form = new FormData();
            form.append('organization_id', orgId);
            form.append('kind', libraryKind);
            form.append('name', libraryName || label);
            form.append('image', offer);
            const res = await fetch(`${API_BASE}/api/brand-assets`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${getToken()}` },
                body: form,
            });
            if (!res.ok) throw new Error('library');
            toast.success(t('brandLibrary.added'));
            setOffer(null);
        } catch (_e) {
            toast.error(t('brandLibrary.addFailed'));
        } finally { setSaving(false); }
    };

    return (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3"
             data-testid={`${testid}-field`}>
            <p className="text-xs font-medium text-slate-500">{label}</p>
            {hint && <p className="text-[11px] text-slate-400 mb-2">{hint}</p>}
            <div className="flex items-center gap-3 mt-2">
                <div className="w-16 h-16 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden shrink-0">
                    {value
                        ? <img src={abs(value)} alt="" className="max-w-full max-h-full object-contain"
                               data-testid={`${testid}-preview`} />
                        : <ImageIcon size={18} className="text-slate-400" />}
                </div>
                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => input.current?.click()} disabled={busy}
                            data-testid={`${testid}-upload`}
                            className={btn.smallSecondary}>
                        {busy ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />} Upload
                    </button>
                    {onLibrary && (
                        <button type="button" onClick={onLibrary} data-testid={`${testid}-library`}
                                className={btn.smallSecondary}>
                            <ImageIcon size={13} /> Brand library
                        </button>
                    )}
                    {value && onChange && (
                        <button type="button" onClick={() => onChange('')} data-testid={`${testid}-remove`}
                                className={btn.iconDanger + " px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5"}>
                            <Trash2 size={13} /> Remove
                        </button>
                    )}
                </div>
            </div>

            {libraryNote && (
                <p className="text-[11px] text-slate-500 mt-2" data-testid={`${testid}-library-note`}>
                    {t('brandLibrary.savedNote')}
                </p>
            )}

            {offer && (
                <div className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-3"
                     data-testid={`${testid}-library-offer`}>
                    <p className="text-xs font-medium text-slate-700 dark:text-slate-200">
                        {t('brandLibrary.addPrompt')}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                        <button type="button" onClick={addToLibrary} disabled={saving}
                                data-testid={`${testid}-library-add`}
                                className="min-h-[44px] rounded-lg bg-slate-900 dark:bg-white dark:text-slate-900 text-white px-4 text-sm font-semibold inline-flex items-center gap-2">
                            {saving && <Loader2 className="animate-spin" size={14} />}
                            {t('brandLibrary.addNow')}
                        </button>
                        <button type="button" onClick={() => setOffer(null)} disabled={saving}
                                data-testid={`${testid}-library-skip`}
                                className="min-h-[44px] rounded-lg border border-slate-300 dark:border-slate-600 px-4 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            {t('brandLibrary.notNow')}
                        </button>
                    </div>
                </div>
            )}

            <input ref={input} type="file" accept="image/*" onChange={upload} className="hidden" />
        </div>
    );
};

export default ImageField;
